import '../styles/web.css'

import {
  currentRouteName,
  enableScreenKeepAlive,
  initShell,
  navigate,
  setDetailOpener,
  setOverlay,
  type Route,
  type Screen,
} from '@/app'
import { onSelectedProfileGone, refreshAge, resetAge, startAgeWatch } from '@/lib/ageGate'
import { type ContentCard, getHome, type Home, loadUpcomingAvailability } from '@/lib/catalog'
import { type ContinueCard, getContinueWatching } from '@/lib/continue'
import { byId, clear, el } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { loadMediaConfig } from '@/lib/images'
import { resolveFirstLaunchLanguage } from '@/lib/geoLanguage'
import { onLanguageChange, readLanguage, syncDocumentLanguage } from '@/lib/language'
import {
  applyViewerAgeLimit,
  isKidsProfile,
  isRealProfileId,
  readSelectedProfileId,
  restoreViewerAgeLimit,
  writeSelectedProfileId,
} from '@/lib/profiles'
import { getRecommended } from '@/lib/recommended'
import { AUTH_MODE, readSession } from '@/lib/session'
import { loadPublicSettings } from '@/lib/settings'
import { getWatchlist, onWatchlistChange, watchlistGeneration, type WatchlistChange } from '@/lib/watchlist'
import { createPlayerScreen } from '@/screens/player'
import { initWebShell } from '@/browser/shell'
import { createWebQrScreen, createWebProfileScreen, createWebProfilesScreen } from '@/browser/screens/account'
import {
  createWebCollectionsScreen,
  createWebBrowseScreen,
  createWebListScreen,
  createWebSearchScreen,
  createWebUpcomingScreen,
} from '@/browser/screens/catalog'
import { closeWebDetail, isWebDetailOpen, openWebDetail, openWebDetailBySlug, openWebDetailGated } from '@/browser/detailModal'
import { restoreFromLocation, setModalBridge } from '@/browser/history'
import { createWebDetailScreen } from '@/browser/screens/detail'
import { createWebHomeScreen } from '@/browser/screens/home'
import { createWebKidsHomeScreen } from '@/browser/screens/kidsHome'
import { createWebLiveScreen } from '@/browser/screens/live'
import { createWebPersonScreen } from '@/browser/screens/person'
import { closeTopWebModal, webEmpty } from '@/browser/ui'

/**
 * BRAUZER VARİANTININ GİRİŞ NÖQTƏSİ (`browser.html` → bu fayl).
 *
 * Cihaz app-ının qarşılığı `src/main.ts`-dir. İkisi arasındakı fərq və səbəbi
 * `lib/platform.ts`-in başındadır.
 *
 * ⚠ NƏ ORTAQDIR: bütün data qatı (`lib/*`), marşrutlaşdırıcı (`app.ts`) və
 *   OYNADICI (`screens/player.ts`). Yalnız ekranların çəkilişi ayrıdır.
 * ⚠ NƏ İŞLƏDİLMİR: `lib/input.ts` kadr nasosu, `lib/focus.ts` şəbəkəsi,
 *   `lib/canvas.ts` sütun arifmetikası, `ui/cardPreview.ts` — hamısı sabit
 *   1920×1080 kətan üçün yazılıb və bu variantda mənasızdır.
 */

let home: Home | null = null
let recommended: ContentCard[] = []
let continueWatching: ContinueCard[] = []
let watchlist: ContentCard[] = []

/** Sıraya çəkilən tövsiyə sayı — brauzerdə sıra uzun ola bilər, sürüşmə doğmadır. */
const RECOMMENDED_LIMIT = 20

function boot(): void {
  // `<html lang>` saxlancdakı seçimlə eyniləşdirilir (2026-08-16 — veb TV 42):
  // əvvəl atribut sənəddə sabit «az» idi və dil dəyişəndə də dəyişmirdi.
  syncDocumentLanguage()
  paintBootHint()
  // Uşaq profilinin süzgəci və teması saxlancdan bərpa olunur (izahı `lib/profiles`).
  restoreViewerAgeLimit()
  initShell(createScreen)
  /*
    2026-10-23: «Geri» əvvəlki ekranı YENİDƏN QURMUR — saxlanmış nüsxəni qaytarır (izah
      `app.ts` → `enableScreenKeepAlive`). Açar dəyişibsə (dil · profil · uşaq qatı · hesab)
      nüsxə atılır və ekran yenidən qurulur: böyük profilin ana ekranı uşaq profilinə,
      köhnə dilin mətnləri yeni dilə keçməsin.
  */
  enableScreenKeepAlive(() => {
    const session = readSession()
    return [
      readLanguage(),
      readSelectedProfileId() || '',
      document.documentElement.getAttribute('data-kids') || '',
      session ? session.userId : '',
    ].join('|')
  })
  initWebShell()
  /*
    ⚠⚠ 2026-08-13-DƏN FİLM MODALDA AÇILIR (sahib tələbi: «vebtvdə film item
       klikdə tv projelər kimi açsın modalı»). Əvvəl burada `detail` marşrutu
       açılırdı — yəni oynadıcıdan çıxanda FİLM SƏHİFƏSİ qurulurdu.
    ⚠ FİLM SƏHİFƏSİ SİLİNMİR: `detail` marşrutu ehtiyat yol kimi qalır
      (`createScreen` → `createWebDetailScreen`), sadəcə ona keçid verilmir.
  */
  // 2026-10-25: seed kartla (sonluq təklifi yaşa görə bağlıdır — `player.ts` → `playSimilar`)
  //   açılış YAŞ QAPISINDAN keçir; oynadıcıdan adi qayıdış qapısız qalır.
  setDetailOpener((slug: string, trailerAt?: number, seed?: ContentCard) =>
    seed ? openWebDetail(seed) : openWebDetailBySlug(slug, trailerAt),
  )
  // Brauzerin «Geri»si və `#/film/slug` bərpası modalı bu körpü ilə idarə edir.
  // ⚠ `openBySlug` YAŞ QAPISINDAN KEÇİR (2026-10-23) — `#/film/<slug>` bərpası, ünvan çubuğu
  //   və «İrəli» yeni açılışdır; izah `detailModal.ts` → `openWebDetailGated`. Oynadıcıdan
  //   QAYIDIŞ isə yuxarıdakı `setDetailOpener`-dir (qapısız — film artıq açılıb).
  // ⚠ `closeDialog` — brauzerin «Geri»si əvvəlcə yaş pəncərəsini bağlayır (`ui.ts`).
  setModalBridge({
    isOpen: isWebDetailOpen,
    close: closeWebDetail,
    openBySlug: (slug: string) => openWebDetailGated(slug),
    closeDialog: closeTopWebModal,
  })
  // «Sonra İzlə» düyməsi ilə boot keşini uzlaşdırır — izahı `syncWatchlist`-dədir.
  onWatchlistChange(syncWatchlist)
  // Dil dəyişəndə eyni keş yenidən çəkilir — izahı `reloadHomeData`-dadır.
  onLanguageChange(reloadHomeData)
  watchAgeState()
  watchSelectedProfile()

  // ⚠ PARALEL: iki sorğunun cəmi deyil, ƏN UZUNU gözlənilir. `loadMediaConfig`
  //   heç vaxt reject etmir — alınmasa build fallback-ı ilə davam edilir.
  // ⚠ MEDİA KONFİQURASİYASI EKRANDAN ƏVVƏL: şəkil ünvanının origin-i ilk kartdan
  //   əvvəl məlum olmalıdır (`lib/images.ts`).
  // ⚠ PUBLİK PARAMETRLƏR DƏ BURADADIR (2026-08-30): üst zolağın «Canlı» bəndi
  //   `applyRoute`-da dəqiqləşir, o da ilk `navigate()` ilə — yəni cavab bu
  //   `Promise.all` bitənə qədər gəlməlidir (`lib/settings.ts`). Sorğu anonimdir və
  //   heç vaxt reject etmir; sonda əlavə olunub ki, `result[i]` indeksləri dəyişməsin.
  // İlk açılış dili boot sorğularından ƏVVƏL (BR 26.4) — izah `lib/geoLanguage.ts`-dədir.
  void resolveFirstLaunchLanguage().then(() => Promise.all([
    loadMediaConfig(),
    getHome(),
    getRecommended(RECOMMENDED_LIMIT),
    getContinueWatching(RECOMMENDED_LIMIT),
    getWatchlist(),
    loadPublicSettings(),
    // «Tezliklə» menyu bəndi — sonda, `result[i]` indeksləri dəyişməsin (izah `lib/catalog.ts`).
    loadUpcomingAvailability(),
  ])).then((result) => {
    home = result[1]
    recommended = result[2]
    continueWatching = result[3]
    watchlist = result[4]
    byId('boot').hidden = true
    const first = firstRoute()
    /*
      ⚠ YENİLƏMƏ EYNİ EKRANA QAYIDIR (2026-09-25, istifadəçi testi: «səhifəni refresh
        edəndə baş səhifəyə qayıdır»): giriş və profil tamamdırsa ünvandakı ekran
        (`#/browse`, `#/person/x`, `#/film/x` …) bərpa olunur. Giriş axını (QR ·
        «Kim baxır?») ünvandan asılı deyil — ünvan yalnız ondan sonra oxunur.
    */
    if (first.name === 'home' && restoreFromLocation()) return
    navigate(first)
  }).catch(showBootError)
}

/**
 * BOOT SINANDA (2026-09-26): `Promise.all(...).then(...)`-in `.catch`-i yox idi — girişlər
 * reject etmir, amma `.then` içində ekran qurularkən atılan istisna boot skeletonunu
 * ƏBƏDİ saxlayırdı (mesajsız «app dondu»). İndi mesaj + «Yenidən cəhd et» (səhifə yenilənir).
 */
function showBootError(error: unknown): void {
  const boot = byId('boot')
  clear(boot)
  boot.hidden = false
  const box = el('div', 'booterror')
  box.appendChild(el('p', 'booterror__title', T('Yükləmə alınmadı.')))
  const detail = error instanceof Error ? error.message : ''
  if (detail) box.appendChild(el('p', 'booterror__detail', detail))
  const retry = el('div', 'btn btn--primary focusable is-focused', T('Yenidən cəhd et'))
  retry.addEventListener('click', () => window.location.reload())
  box.appendChild(retry)
  boot.appendChild(box)
  setOverlay({
    onKey(key) {
      if (key === 'enter') window.location.reload()
    },
  })
}

/**
 * «Sonra İzlə» KEŞİNİ DİRİ SAXLAYIR — cihaz `main.ts`-dəki `syncWatchlist`-in
 * eynisi (səbəb və qaydalar orada yazılıb: siyahı boot-da bir dəfə çəkilir,
 * ekran isə öz nüsxəsini saxlayır).
 *
 * ⚠ NÜSXƏ QƏSDƏNDİR: iki giriş nöqtəsinin ORTAQ modulu yoxdur (biri cihaz, o biri
 *   brauzer bundle-ıdır) və keş dəyişəni hər birinin öz modul vəziyyətidir —
 *   ortaq funksiya yazmaq üçün keşi `lib/`-ə köçürmək lazım gələrdi, bu isə boot
 *   yayımını yenidən qurmaq deməkdir.
 */
function syncWatchlist(change: WatchlistChange): void {
  const next: ContentCard[] = []
  if (change.added) {
    if (!change.card) return
    next.push(change.card)
  }
  for (let i = 0; i < watchlist.length; i++) {
    const card = watchlist[i] as ContentCard
    if (card.id !== change.contentId) next.push(card)
  }
  watchlist = next
}

/**
 * BOOT KEŞİNİ CARİ DİLDƏ YENİDƏN ÇƏKİR — cihaz `main.ts`-dəki `reloadHomeData`-nın
 * eynisi (ölçmə, «çağıranı gözlətmə» qərarı və `syncWatchlist` ilə münasibət
 * ORADA yazılıb; nüsxə olmasının səbəbi `syncWatchlist`-dəki qeyddir — iki giriş
 * nöqtəsinin ortaq modul vəziyyəti yoxdur).
 *
 * ⚠ BURADA DİL ÜST ZOLAQDAN DA DƏYİŞİR (`browser/shell.ts` → `pickLanguage`), yəni
 *   istifadəçi ANA EKRANIN ÖZÜNDƏ ola bilər — cihaz hədəfində seçim yalnız Profil
 *   ekranındadır. Ona görə «ekranda ana ekran varsa yenidən qur» şərti burada
 *   nəzəri deyil, adi haldır.
 * ⚠ ÜÇ MÜHAFİZƏ (sıra · öz çevirməmiz · yer) cihaz `main.ts`-dəki eynidir —
 *   ölçmə və səbəblər ORADA yazılıb; kod nüsxədir, çünki keş hər giriş nöqtəsinin
 *   ÖZ modul vəziyyətidir (yuxarıdakı `syncWatchlist` qeydi).
 */
let reloadToken = 0

function reloadHomeData(): Promise<void> {
  const token = ++reloadToken
  const asked = watchlistGeneration()
  const from = currentRouteName()
  return Promise.all([
    getHome(),
    getRecommended(RECOMMENDED_LIMIT),
    getContinueWatching(RECOMMENDED_LIMIT),
    getWatchlist(),
  ]).then((result) => {
    if (token !== reloadToken) return
    home = result[0]
    recommended = result[1]
    continueWatching = result[2]
    if (watchlistGeneration() === asked) watchlist = result[3]
    if (from === 'home' && currentRouteName() === 'home') navigate({ name: 'home' }, true)
  })
}

/**
 * Boot skeletonunun «Yüklənir…» sətri (`browser.html`) — cari dildə.
 * ⚠ Səbəb və qayda cihaz `main.ts`-dəki eyni funksiyada yazılıb: markup JS-siz
 *   çəkilir, ona görə orada azərbaycanca qalır və bundle işə düşən kimi yenilənir.
 */
function paintBootHint(): void {
  const hint = document.querySelector('#boot .hint')
  if (hint) hint.textContent = T('Yüklənir…')
}

/**
 * Boot marşrutu — cihaz app-ı ilə eynidir:
 *   giriş yoxdur                 → QR
 *   giriş var, profil seçilməyib → «Kim baxır?»
 *   giriş var, profil seçilib    → ana ekran
 */
function firstRoute(): Route {
  if (!readSession()) return { name: 'qr' }
  const profileId = readSelectedProfileId()
  const valid = AUTH_MODE === 'mock' ? Boolean(profileId) : isRealProfileId(profileId)
  if (!valid) return { name: 'profiles', replaceOnPick: true }
  return { name: 'home' }
}

/**
 * BOOT KEŞİ HANSI PROFİLƏ AİDDİR — cihaz `main.ts`-dəki eyni mühafizə (ölçmə və
 * səbəblər orada yazılıb: profil dəyişəndə boot yenidən işləmir, ana ekranda isə
 * ƏVVƏLKİ profilin «Sonra İzlə» sırası qalırdı). Brauzerdə profil seçimi
 * `browser/screens/account.ts` → `tile()`-dədir.
 */
let cacheProfileId = readSelectedProfileId()

function homeScreen(): Screen {
  const current = readSelectedProfileId()
  if (current !== cacheProfileId) {
    cacheProfileId = current
    /*
      ⚠⚠ KATALOQ DA ATILIR, TƏKCƏ «Sonra İzlə» DEYİL (2026-08-30 — TestingAI veb
         TV 70). NƏ ÖLÇÜLDÜ (yerli dev, canlı API): yaş həddi düzəldiləndən sonra
         da böyük profildən uşaq profilinə keçəndə İLK kadrda ekranda ƏVVƏLKİ
         profilin keşi qalırdı — +200 ms-də 190 kart, onlardan 59-u 16+/18+
         (süzülmüş cavab yalnız ~+1 s-də gəlirdi). Tərs istiqamətdə də böyük
         profil ~400 ms uşaq kataloqunu (163 kart) görürdü.
         ƏVVƏL: yalnız `watchlist` sıfırlanırdı — kataloq isə A profilinin
         cavabından B profilinin ekranına olduğu kimi keçirdi.
      ⚠ EKRAN BOŞ QALMIR: `home === null` halında aşağıda «Yüklənir…» çəkilir və
        `reloadHomeData()` data gələn kimi ekranı yenidən qurur (izahı orada) —
        qara ekran/yanıldıcı «kataloq yüklənmədi» mesajı yaranmır (§6).
    */
    home = null
    recommended = []
    continueWatching = []
    watchlist = []
    void reloadHomeData()
  }
  // Profil dəyişikliyinin sorğusu yoldadır — yuxarıdakı qeyd.
  if (!home) return loadingScreen()
  /*
    ⚠⚠ UŞAQ PROFİLİNDƏ AYRICA EKRAN (2026-08-30 — TestingAI veb TV 67).
       NƏ ÖLÇÜLDÜ: veb TV-də uşaq profili «Ləman» (isKids, maxAgeRating 12) seçilib
       səhifə yeniləndi — səhnə ADİ ana ekranı çəkdi (ilk düyün `web-page`, adi
       hero, «Sənin üçün / Yeni filmlər / Ən çox baxılanlar» sıraları, 163
       `.web-card`), nə «Salam, …!» salamlaması, nə «Nə baxaq?» kartları vardı.
       ƏVVƏL NƏ OLURDU: bu funksiya HƏMİŞƏ `createWebHomeScreen` çağırırdı və
       `src/browser/` altında «kids» sözü ümumiyyətlə yox idi — uşaq ekranı yalnız
       cihaz giriş nöqtəsinə (`src/main.ts`) bağlanmışdı.
    ⚠ ŞƏRT ADİ EKRANIN İÇİNƏ QOYULMUR — cihaz `main.ts`-dəki eyni qərar: iki qurucu
      yan-yana durur, `isKidsProfile()` birini seçir (sahibin «normal profilin də
      içini sikmisən» şikayəti adi ekranı şərtlə doldurmaqdan çıxmışdı).
  */
  return (isKidsProfile() ? createWebKidsHomeScreen : createWebHomeScreen)(
    home,
    recommended,
    continueWatching,
    watchlist,
  )
}

/** Profil dəyişikliyindən sonrakı qısa gözləmə — izahı `homeScreen`-dədir. */
function loadingScreen(): Screen {
  const node = webEmpty(T('Yüklənir…'))
  return {
    node,
    takeFocus() {},
    releaseFocus() {},
    onKey() {},
    destroy() {},
  }
}

function createScreen(route: Route): Screen {
  switch (route.name) {
    case 'home':
      return homeScreen()
    case 'live':
      return createWebLiveScreen()
    case 'search':
      return createWebSearchScreen()
    case 'collections':
      return createWebCollectionsScreen()
    case 'browse':
      return createWebBrowseScreen()
    case 'upcoming':
      return createWebUpcomingScreen()
    case 'list':
      return createWebListScreen({ kind: route.kind, slug: route.slug, title: route.title })
    case 'profile':
      return createWebProfileScreen()
    case 'profiles':
      return createWebProfilesScreen(route.replaceOnPick)
    case 'detail':
      return createWebDetailScreen(route.slug)
    // ⚠ 2026-08-16-DA ƏLAVƏ OLUNDU (TestingClaude veb TV 39): `person` marşrutu
    //   `Route` tipində vardı, burada isə tutulmurdu → axtarışdan açılan şəxs
    //   `fallbackScreen()`-ə, yəni boş ekrana düşürdü. Cihaz hədəfindəki
    //   `main.ts:206 case 'person'` sətrinin qarşılığıdır.
    case 'person':
      return createWebPersonScreen(route.slug)
    case 'qr':
      return createWebQrScreen(() => {
        writeSelectedProfileId(null)
        // Yeni hesab — uşaq süzgəci və teması da sıfırlanır (profil yenidən seçiləcək).
        applyViewerAgeLimit(null)
        // ⚠ Yaş keşi sıfırlanır (MYTasks 577): yeni bağlanan hesab ƏVVƏLKİ
        //   istifadəçinin yaşı ilə qiymətləndirilməməlidir.
        resetAge()
        startAgeWatch()
        navigate({ name: 'profiles', replaceOnPick: true }, true)
      })
    case 'player':
      // ⚠ ORTAQ OYNADICI — «kino rejimi»ndə miqyaslanan kətanda çəkilir
      //   (`browser/shell.ts` → `setCinema`).
      return createPlayerScreen({
        slug: route.slug,
        title: route.title,
        contentId: route.contentId,
        trailerAsset: route.trailerAsset,
        trailerStartAt: route.trailerStartAt,
        fromHero: route.fromHero,
        live: route.live,
        liveProgramId: route.liveProgramId,
        episodeId: route.episodeId,
        episodes: route.episodes,
        similar: route.similar,
        outroSeconds: route.outroSeconds,
        ageLimit: route.ageLimit,
      })
    // `sessions` marşrutu cihaz app-ında da menyudan çıxarılıb (2026-07-29) —
    // brauzerdə ekran qurulmur, dead-end də yaranmır (ana ekrana düşür).
    default:
      return fallbackScreen()
  }
}

function fallbackScreen(): Screen {
  const node = webEmpty('')
  return {
    node,
    takeFocus() {},
    releaseFocus() {},
    onKey() {},
    destroy() {},
  }
}

/**
 * Yaş vəziyyətinin canlı izlənməsi (MYTasks 577) — cihaz app-ındakı eyni iki qat:
 * long-poll + `visibilitychange`.
 */
function watchAgeState(): void {
  startAgeWatch()
  document.addEventListener(
    'visibilitychange',
    function () {
      if (document.hidden) return
      void refreshAge()
      startAgeWatch()
    },
    false,
  )
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, false)
} else {
  boot()
}

/**
 * Seçilmiş profil başqa cihazda silinəndə (yaş vəziyyətinin profil siyahısında yoxdur,
 * `lib/ageGate.ts` → `applyKidsLimit`) «Kim baxır?» açılır — 2026-10-25. ƏVVƏL seçim qalırdı
 * və hər «İzlə» «video hələ hazırlanmayıb» verirdi.
 */
function watchSelectedProfile(): void {
  onSelectedProfileGone(() => {
    if (!readSession()) return
    const current = currentRouteName()
    if (current === 'profiles' || current === 'qr') return
    navigate({ name: 'profiles', replaceOnPick: true }, true)
  })
}
